import type { ItemProps } from 'react-photoswipe-gallery';
import type { ProductImageType, ProductVideoType } from 'autogen/swagger/all';
import type { ScrollType } from 'components/emblaCarousel';
import type { IVideo } from 'components/video/shared/types';

export interface ImageMediaItem extends Omit<ItemProps<HTMLElement>, 'children'> {
    type: 'image';
    original: string;
    thumbnail: string;
    productImageType?: ProductImageType; // should not be part of generic imageMediaItem
}

export interface VideoMediaItem extends Omit<ItemProps<HTMLElement>, 'children' | 'content'> {
    type: 'video';
    video: IVideo;
    productVideoType?: ProductVideoType; // type should not be part of generic VideoMediaItem
}

export interface HighlightedVideoMediaItem extends Omit<ItemProps<HTMLElement>, 'children' | 'content'> {
    type: 'highlightedVideo';
    original: string;
    video: IVideo;
    thumbnail: string;
}

export interface Product360MediaItem extends Omit<ItemProps<HTMLElement>, 'children'> {
    type: 'product360';
    directoryUrl: string;
    thumbnail: string;
}

export type GalleryMediaItem = ImageMediaItem | VideoMediaItem | HighlightedVideoMediaItem | Product360MediaItem;

export type GalleryMediaItems = GalleryMediaItem[];

export function MediaItemDiscriminator<T>(
    item: GalleryMediaItem,
    funcImageMediaItem: (m: ImageMediaItem) => T,
    funcVideoMediaItem: (m: VideoMediaItem) => T,
    funcHighlightedVideoMediaItem: (m: HighlightedVideoMediaItem) => T,
    func360MediaItem: (m: Product360MediaItem) => T,
    defaultFunc: () => T
): T {
    switch (item.type) {
        case 'image':
            return funcImageMediaItem(item as ImageMediaItem);
        case 'video':
            return funcVideoMediaItem(item as VideoMediaItem);
        case 'highlightedVideo':
            return funcHighlightedVideoMediaItem(item as HighlightedVideoMediaItem);
        case 'product360':
            return func360MediaItem(item as Product360MediaItem);
        default:
            return defaultFunc();
    }
}

export type GalleryPreviewerOptions = {
    visibleCarouselThumbnails?: number;
    hideGalleryThumbnailCarousel?: boolean;
    hidePagination?: boolean;
};

export type onThumbnailClick = ({ index, type }: { index: number; type: GalleryMediaItem['type'] }) => void;

export type onPaginate = (index: number, type?: GalleryMediaItem['type']) => void;

export type onCarouselScrollClick = (type: ScrollType) => void;
